// @ts-strict-ignore
import React, { useContext } from 'react';
import { Controller } from 'react-hook-form';
import type { Option } from '@usealloy/component-library';
import {
  TextField,
  MultiSelect,
  useSnackBar,
} from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import type { FormattedServiceConfigOption } from './types';
import {
  CredentialOwner,
} from './types';
import {
  StyledTextField,
} from './styles';
import type { Operation } from 'flow/service/changelog/types';
import {
  getDiffStyleFunction,
  StyledTextField as DiffTextFieldWrapper,
  withDiffStyles,
  withMultiItemDiffStyles,
} from 'flow/editor/components/NodeEditorDrawer/diffStyles';

const normalizeWhatIfBooleanOption = (option: Option | undefined): Option | undefined => {
  if (!option) {
    return option;
  }

  const optionValue = String(option.value || '').toLowerCase();
  if (optionValue === 'true' || optionValue === 'false') {
    return option;
  }

  return { label: 'True', value: 'true' };
};

interface ServiceConfigOptionInputFieldProps {
  serviceConfigOptionMeta;
  formattedServiceConfigOption: FormattedServiceConfigOption;
  control;
  index: number;
  isUsingDefault: boolean;
  hasError: boolean;
  trigger;
  setValue;
  credentialOwner: CredentialOwner;
  isSecret: boolean;
  submitAutoSave?: () => {};
  onlySandboxEnabled?: boolean;
  isReadOnly?: boolean;
  isSnippetEditor?: boolean;
  diffStatus?: Operation | null;
}

export const ServiceConfigOptionInputField = ({
  serviceConfigOptionMeta,
  formattedServiceConfigOption,
  control,
  index,
  isUsingDefault,
  hasError,
  trigger,
  setValue,
  credentialOwner,
  isSecret,
  submitAutoSave,
  onlySandboxEnabled,
  isReadOnly,
  isSnippetEditor,
  diffStatus = null,
}: ServiceConfigOptionInputFieldProps) => {
  const addSnackBar = useSnackBar();
  const { agent: { isAgent } } = useContext(UserContext);

  const isAlloyCredential = credentialOwner === CredentialOwner.alloy;
  const isUnknownCredential = !credentialOwner || credentialOwner === CredentialOwner.unknown;

  // disabled for the customer agent editing the alloy or unknown owner field
  const agentSecretDisabled = isAgent && isSecret && (isAlloyCredential || isUnknownCredential);

  const isSecretInSnippet = Boolean(isSnippetEditor && isSecret);
  const isAuthInSnippet = Boolean(
    isSnippetEditor && serviceConfigOptionMeta?.category === 'auth',
  );
  const isSnippetRestricted = isSecretInSnippet || isAuthInSnippet;

  const allPossibleValue = serviceConfigOptionMeta?.possibleValues?.map(
    pv => ({
      label: pv.displayName,
      value: pv.serviceConfigPossibleValueId.toString(),
    }),
  );


  switch (serviceConfigOptionMeta.datatype) {
    case 'number':
    case 'decimal':
      return (
        <Controller
          control={control}
          name={`formattedServiceConfigOptions.${index}.value` as const}
          rules={{
            required: (
              !onlySandboxEnabled
              && serviceConfigOptionMeta.isRequired
              && !isUsingDefault
              && !isSnippetRestricted
            )
              ? `${serviceConfigOptionMeta.displayName} cannot be empty.` as const
              : false,
          }}
          render={({ field: { onChange, value } }) => (
            <DiffTextFieldWrapper diffStatus={diffStatus}>
              <StyledTextField
                type="number"
                key={formattedServiceConfigOption.id}
                id={`${formattedServiceConfigOption.serviceConfigOptionId}`}
                step={serviceConfigOptionMeta.datatype === 'number' ? 1 : 0.1}
                label={serviceConfigOptionMeta.displayName}
                autoComplete="stopItChrome"
                required={
                  !onlySandboxEnabled
                  && serviceConfigOptionMeta.isRequired
                  && !isSnippetRestricted
                }
                error={hasError}
                value={value}
                disabled={isReadOnly || isSnippetRestricted}
                onChange={newValue => {
                  onChange(Number(newValue.target.value));
                  setValue(
                    `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
                    false,
                  );
                  trigger(`formattedServiceConfigOptions.${index}.value` as const);
                  if (submitAutoSave) {
                    submitAutoSave();
                  }
                }}
              />
            </DiffTextFieldWrapper>
          )}
        />
      );
    case 'boolean':
      return (
        <Controller
          control={control}
          name={`formattedServiceConfigOptions.${index}.value` as const}
          rules={{
            required: (!onlySandboxEnabled && serviceConfigOptionMeta.isRequired
              && !isUsingDefault && !isSnippetRestricted)
              ? `${serviceConfigOptionMeta.displayName} cannot be empty.` as const : false,
          }}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              label={serviceConfigOptionMeta.displayName}
              key={formattedServiceConfigOption.id}
              required={
                !onlySandboxEnabled
                && serviceConfigOptionMeta.isRequired
                && !isSnippetRestricted
              }
              error={hasError}
              onChange={(newOption: Option) => {
                setValue(`formattedServiceConfigOptions.${index}.value` as const, newOption);
                setValue(
                  `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
                  false,
                );
                onChange(newOption);
                if (submitAutoSave) {
                  submitAutoSave();
                }
              }}
              options={[
                { label: 'True', value: 'true' },
                { label: 'False', value: 'false' },
              ]}
              selectedOptions={
                serviceConfigOptionMeta.slug === 'what_if_enabled'
                  ? normalizeWhatIfBooleanOption(value)
                  : value
              }
              isMulti={false}
              isDisabled={isReadOnly || isSnippetRestricted}
              customStyleFunctions={getDiffStyleFunction(diffStatus)}
            />
          )}
        />
      );
    case 'singleselect':
    case 'multiselect':
      return (
        <Controller
          control={control}
          name={`formattedServiceConfigOptions.${index}.value` as const}
          rules={{
            validate: value => (
              onlySandboxEnabled || isSnippetRestricted || value?.length >= 1
              || `${serviceConfigOptionMeta.displayName} cannot be empty.` as const
            ),
          }}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              label={serviceConfigOptionMeta.displayName}
              key={formattedServiceConfigOption.id}
              required={
                !onlySandboxEnabled
                && serviceConfigOptionMeta.isRequired
                && !isSnippetRestricted
              }
              error={hasError}
              onChange={newOptions => {
                if (serviceConfigOptionMeta.datatype === 'singleselect') {
                  onChange([newOptions]);
                  setValue(
                    `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
                    false,
                  );
                  trigger(`formattedServiceConfigOptions.${index}.value` as const);
                  if (submitAutoSave) {
                    submitAutoSave();
                  }
                // @ts-ignore
                } else if (newOptions?.length >= 1) {
                  onChange(newOptions);
                  setValue(
                    `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
                    false,
                  );
                  trigger(`formattedServiceConfigOptions.${index}.value` as const);
                  if (submitAutoSave) {
                    submitAutoSave();
                  }
                } else {
                  addSnackBar({
                    title: `${serviceConfigOptionMeta.displayName} cannot be empty.` as const,
                    type: 'danger',
                  });
                }
              }}
              options={allPossibleValue}
              isSearchable
              selectedOptions={value}
              visibleSelectedOptions={serviceConfigOptionMeta.datatype === 'multiselect'}
              isMulti={serviceConfigOptionMeta.datatype === 'multiselect'}
              isDisabled={isReadOnly || isSnippetRestricted}
              dynamicMenuHeight
              // Multiselect shows chips, so color those by diff status as well as the control.
              {...withMultiItemDiffStyles(
                withDiffStyles({}, diffStatus),
                () => diffStatus,
              )}
            />
          )}
        />
      );
    default:
      return (
        <Controller
          control={control}
          name={`formattedServiceConfigOptions.${index}.value` as const}
          rules={{
            validate: value => (
              onlySandboxEnabled || isSnippetRestricted || value?.length >= 1
              || `${serviceConfigOptionMeta.displayName} cannot be empty.` as const
            ),
          }}
          render={({ field: { onChange, value } }) => {
            const finalValue = (isAgent && isSecret && isAlloyCredential) ? '•••••••••••' : value;
            return (
              <DiffTextFieldWrapper diffStatus={diffStatus}>
                <TextField
                  type="text"
                  key={formattedServiceConfigOption.id}
                  id={`${serviceConfigOptionMeta.serviceConfigOptionId}`}
                  label={serviceConfigOptionMeta.displayName}
                  autoComplete="stopItChrome"
                  isSecret={isSecret && !agentSecretDisabled}
                  required={
                    !onlySandboxEnabled
                    && serviceConfigOptionMeta.isRequired
                    && !isSnippetRestricted
                  }
                  disabled={agentSecretDisabled || isSnippetRestricted || isReadOnly}
                  error={hasError}
                  value={finalValue}
                  onBlur={() => {
                    trigger(`formattedServiceConfigOptions.${index}.value` as const);
                  }}
                  onChange={newValue => {
                    onChange(newValue);
                    setValue(
                      `formattedServiceConfigOptions.${index}.isUsingDefault` as const,
                      false,
                    );
                    trigger(`formattedServiceConfigOptions.${index}.value` as const);
                    if (submitAutoSave) {
                      submitAutoSave();
                    }
                  }}
                />
              </DiffTextFieldWrapper>
            );
          }}
        />
      );
  }
};
